<script setup lang="ts">
import { Link, usePage } from "@inertiajs/vue3";
import { defineEmits, ref } from "vue";

import Btn from "@/components/ui/atoms/Btn.vue";
import { type InertiaSharedProps } from "@/shared/types";
import { resolveUrl } from "@/utils/urls";

const page = usePage<InertiaSharedProps>();
const user = ref(page.props.user);
const emit = defineEmits(["openSidebar", "toggleMenu"]);

interface Props {
  expand?: boolean;
}

withDefaults(defineProps<Props>(), {
  expand: false,
});

const mobileMenuOpen = ref(false);
const toggleMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  emit("toggleMenu");
};

const openLogin = () => {
  mobileMenuOpen.value = false;
  emit("openSidebar");
};
</script>

<template>
  <div>
    <header class="header">
      <div class="header__container">
        <section :class="['header__menu', { container: !expand }]">
          <Link href="/">
            <img src="@img/logo.png" alt="Mastermind" class="header__logo" />
          </Link>
          <nav class="header__desktop">
            <Btn
              :class="{ active: page.component === 'Pricing' }"
              tag="a"
              :href="resolveUrl('subscription_show')"
              theme="transparent"
              size="medium"
              :high="true"
            >
              suscripción
            </Btn>
            <Btn
              :class="{ active: page.component === 'Packs' }"
              tag="a"
              :href="resolveUrl('pack_list')"
              theme="transparent"
              size="medium"
              :high="true"
            >
              paquetes y cursos
            </Btn>
            <Btn
              :class="{ active: page.component === 'Faqs' }"
              tag="a"
              href="/#preguntas"
              theme="transparent"
              size="medium"
              :high="true"
            >
              FAQ
            </Btn>
            <Btn
              tag="button"
              type="button"
              v-if="!user"
              theme="green-bold"
              size="medium"
              high
              @click="$emit('openSidebar')"
            >
              login
            </Btn>
            <div v-else>
              <a
                class="pill pill--clickable pill--medium pill--high pill--green-bold pill--square btn-action--generic"
                :href="resolveUrl('account_show')"
              >
                Mi Cuenta
              </a>
            </div>
          </nav>
          <div class="header__mobile" @click="toggleMenu">
            <Btn
              tag="button"
              theme="green-bold"
              size="medium"
              :high="true"
              :class="['header__mobile__menu']"
              shape="square"
            />
          </div>
        </section>
      </div>
    </header>
    <Transition name="fade">
      <div class="mobile-menu" v-if="mobileMenuOpen">
        <div class="header__mobile" @click="toggleMenu">
          <Btn
            tag="button"
            theme="green-bold"
            size="medium"
            :high="true"
            :class="['header__mobile__menu', 'header__mobile__menu--open']"
            shape="square"
          />
        </div>
        <ul class="mobile-menu__links">
          <li>
            <Link
              :class="{ active: page.component === 'Index' }"
              :href="resolveUrl('home_show')"
            >
              Inicio
            </Link>
          </li>
          <li>
            <Link
              :class="{ active: page.component === 'Price' }"
              :href="resolveUrl('subscription_show')"
            >
              Suscripción
            </Link>
          </li>
          <li v-if="false">
            <Link
              :class="{ active: page.component === 'Packs' }"
              :href="resolveUrl('pack_list')"
            >
              Paquetes y Cursos
            </Link>
          </li>
          <li>
            <Link :class="{ active: page.component === 'Faqs' }" href="/#preguntas">
              Preguntas
            </Link>
          </li>
          <li v-if="!user">
            <a @click="openLogin">Entrar</a>
          </li>
          <li v-else>
            <a :href="resolveUrl('account_show')">Mi Cuenta</a>
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/settings/vars";

.header {
  padding: 0;
  height: inherit;

  a.active {
    background-color: vars.$dust;
    border-color: vars.$dust;
    color: vars.$poly-fuchsia;
  }

  &__container {
    width: 100%;
    background-color: vars.$off-white;
    padding: 4px 4px 4px 1rem;
  }

  &__menu {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0;
  }

  &__logo {
    width: 220px;
    height: auto;
    text-decoration: none;
  }

  &__desktop {
    display: none;
    gap: 4px;
    align-items: center;
  }

  &__mobile {
    display: flex;
  }

  @media (min-width: 1024px) {
    &__desktop {
      display: flex;
    }

    &__mobile {
      display: none;
    }
  }
}

.header__mobile__menu {
  width: 3em;
  height: 3em;
  background-image: url("@img/icons/hamburger-icon.svg");
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1em;

  &--open {
    position: absolute;
    margin: 4px;
    top: 0;
    right: 0;
    background-color: vars.$iron;
    background-image: url("@img/icons/close-white.svg");
    z-index: 999;
  }
}

.header__mobile__menu--open:hover {
  background-color: vars.$off-black !important;
}

.mobile-menu {
  position: fixed;
  background-color: vars.$iron;
  top: 0;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  z-index: 99;

  &--visible {
    top: 0;
  }

  &__links {
    margin-top: 4em;
    margin-left: 2em;
    padding: 0;

    li {
      list-style: none;
      text-align: left;
      margin-bottom: 2em;

      a {
        color: vars.$off-white;
        text-decoration: none;
        font-family: vars.$ff_apparat_medium;
        font-size: 2em;
        text-transform: uppercase;
        cursor: pointer;
      }

      .active {
        color: vars.$poly-fuchsia;
      }
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
